import { IAmplifyProvider } from "../hexagon/adapters/driven/providers/amplifyProvider";
import { IlocalStorageProvider } from "../hexagon/adapters/driven/providers/localStorageProvider";
import { IAuthController } from "../hexagon/adapters/driver/authController";
import { useAppDispatch, useAppSelector } from "./redux";
import { setAuthState } from "../redux/Auth/auth.slice";
import {
  setClinicFloorList,
  setSelectedFloors,
  setUserClinic,
  setClinicState,
  setRedirectToUserInfo,
} from "../redux/Clinic/clinic.slice";
import { setLayoutState } from "../redux/Layout/layout.slice";
import { setNotificationSettings } from "../redux/Setting/setting.slice";
import { updateWatchlist } from "../redux/Watchlist/watchlist.slice";
import { featureFlag } from "../constants/feature";
import type { ITimeProvider } from "../hexagon/adapters/driven/providers/provider";
import { AppNotificationType } from "../constants/appNotification";
import type { IAppNotification } from "../types/app-notification";
import { setClinicalAlertFilter } from "../redux/Alert/alert.slice";
import { initialEscalatedAlertLevels } from "../components/CustomComponents/NotificationSettingsList/notification";
import { setDashboardFloor } from "../redux/Dashboard/dashboard.slice";
import { setSummaryState } from "../redux/Summary/summary.slice";
import type { ISummaryController } from "../hexagon/adapters/driver/summaryController";
import { setHomehealthState } from "../redux/Homehealth/homehealth.slice";
import { IHomeHealthController } from "../hexagon/adapters/driver/homeHealthController";
import { ISMSController } from "../hexagon/adapters/driver/smsController";
import { isHomeHealthExternalUser } from "../constants/sso";

export const useRouterHook = ({
  amplifyProvider,
  authController,
  patientController,
  clinicController,
  localStorageProvider,
  notificationController,
  watchlistController,
  timeProvider,
  appNotification,
  circadiaEngineProvider,
  summaryController,
  homeHealthController,
  isPIN,
  smsController,
  userController,
}: {
  amplifyProvider: IAmplifyProvider;
  authController: IAuthController;
  clinicController: IClinicController;
  localStorageProvider: IlocalStorageProvider;
  notificationController: INotificationController;
  watchlistController: IWatchlistController;
  timeProvider: ITimeProvider;
  appNotification: IAppNotification;
  patientController: IPatientController;
  circadiaEngineProvider: ICircadiaEngineProvider;
  summaryController: ISummaryController;
  homeHealthController: IHomeHealthController;
  smsController: ISMSController;
  isPIN: () => boolean;
  userController: IUserController;
}) => {
  let isRedirectToUserInfo = false;
  let storedBiometricValue = null;
  const storedValue = localStorageProvider.get("isBiometric");
  if (storedValue !== null) {
    storedBiometricValue = storedValue === "true";
  }
  const dispatch = useAppDispatch();
  const { dashboardFloor, dotPhraseListResidentNote, persistedUser } =
    useAppSelector((state) => ({
      dashboardFloor: state.dashboard.dashboardFloor,
      clinicList: state.clinic?.clinicList || [],
      dotPhraseListResidentNote: state.summary.dotPhraseListResidentNote || [],
      persistedUser: state.auth.user,
    }));
  const populateAppData = async (changeFacility: boolean = false) => {
    amplifyProvider.amplifyConfigure();
    if (changeFacility) {
      localStorageProvider.remove("dashboard_filters");
    }
    dispatch(
      setLayoutState({
        layoutState: "loading",
      })
    );

    // ── Phase 1: Fire all independent API calls in parallel ──
    const [
      dotPhraseResult,
      response,
      clinicResult,
      smsResult,
      userResult,
      hmacResult,
      betaResult,
    ] = await Promise.all([
      dotPhraseListResidentNote.length === 0 &&
      !isHomeHealthExternalUser(persistedUser?.groups)
        ? summaryController.getDotPhrasesList({ ofType: "all" })
        : Promise.resolve(null),
      notificationController.getNotificationSetting(),
      clinicController.getMyClinics(),
      smsController.getPhoneNumber().catch(() => ({
        success: false,
        data: { phoneNumber: "", isVerified: false },
      })),
      authController.getCurrentUser(),
      isHomeHealthExternalUser(persistedUser?.groups)
        ? Promise.resolve({ success: false, data: null })
        : patientController.getPatientHmac({
            platform: circadiaEngineProvider.plat,
          }),
      userController.getUserBetaFeatures(),
    ]);

    // Dispatch Phase 1 results that have no downstream dependencies
    if (dotPhraseResult?.success && dotPhraseResult?.data) {
      dispatch(
        setSummaryState({
          dotPhraseListResidentNote: dotPhraseResult.data,
        })
      );
    }

    dispatch(
      setAuthState({
        smsData: {
          phoneNumber: smsResult.data?.phoneNumber || "",
          isVerified: smsResult.data?.isVerified || false,
        },
      })
    );

    const user = userResult.data?.user;
    const hmac = hmacResult.data?.data;
    const userEmailFromV4Api = response.data?.data?.email;

    // Update user email from /user/v4 API if not present in Cognito user
    if (user && userEmailFromV4Api && !user.email) {
      user.email = userEmailFromV4Api;
      console.warn(
        "[useRouterHook] Updated user.email from /user/v4 API:",
        userEmailFromV4Api
      );
      dispatch(
        setAuthState({
          user,
        })
      );
    }

    if (betaResult.success && betaResult.data?.data) {
      let betaFeatures = betaResult.data?.data || [];

      // For home_health_external users (or Microsoft SSO users), force-enable third_party_scribe and heidi
      const isHomeHealthExternal = isHomeHealthExternalUser(user?.groups);
      if (isHomeHealthExternal) {
        const forcedFeatures = ["third_party_scribe", "heidi"];
        betaFeatures = [...new Set([...betaFeatures, ...forcedFeatures])];
      }

      if (process.env.NODE_ENV === "development") {
        betaFeatures = [...new Set([...betaFeatures, "medrec"])];
      }

      dispatch(
        setAuthState({
          betaFeaturesAllowedList: betaFeatures,
        })
      );
    }

    // ── Phase 2: Select clinic (needs notification + clinic results) ──
    let getFacilityIdFromNotificationSetting = null;
    if (response.success) {
      getFacilityIdFromNotificationSetting =
        response.data?.data?.notification_preference
          ?.facilities_to_send_notification_for_old_id?.[0];
    }
    const { success: clinicSuccess, data: clinicData } = clinicResult;

    if (clinicSuccess && clinicData) {
      if (clinicData.length === 0) {
        isRedirectToUserInfo = true;
        dispatch(setRedirectToUserInfo(true));
      }
      dispatch(
        setClinicState({
          clinicState: "loading",
          clinicList: clinicData.map((item) => {
            return item;
          }),
        })
      );
      let selectedClinic = null;
      if (
        getFacilityIdFromNotificationSetting &&
        response?.data?.data?.notification_preference
      ) {
        selectedClinic =
          clinicData.find(
            (item) => item.id === Number(getFacilityIdFromNotificationSetting)
          ) || clinicData[0];
        selectedClinic = await clinicController.setSelectedClinic({
          id: selectedClinic?.id || 0,
          clinicList: clinicData,
          notificationSettings: {
            email: response?.data?.data?.notification_preference?.email,
            pushNotification:
              response?.data?.data?.notification_preference?.push_notification,
            kioskMode:
              response?.data?.data?.notification_preference?.kiosk_mode,
            escalatedAlertPreference:
              response?.data?.data?.notification_preference
                ?.escalated_alert_preference,
            facilitiesToSendNotificationForOldId: [selectedClinic?.id || 0],
            psnAlertPreference:
              response?.data?.data?.notification_preference
                ?.psn_alert_preference,
            bedExitPreference:
              response?.data?.data?.notification_preference
                ?.bed_exit_preference,
            sms: response?.data?.data?.notification_preference?.sms,
            smsPreferences:
              response?.data?.data?.notification_preference?.sms_preferences,
          },
        });
      }
      if (!selectedClinic) {
        selectedClinic = clinicController.setDefaultClinic({
          clinicList: clinicData,
        });
      }

      // Profile redirect check (uses Phase 1 getCurrentUser + notifResponse)
      const skipClinicFloorPref = isHomeHealthExternalUser(
        userResult.data?.user?.groups
      );
      const isSSO = localStorageProvider.get("sso") === "true";
      const userInfoUpdatedFlag = localStorageProvider.get("userInfoUpdated");
      let shouldRedirectToUserInfo = false;

      if (isSSO) {
        if (userInfoUpdatedFlag) {
          shouldRedirectToUserInfo = false;
        } else {
          const hasProfileInDB =
            response.data?.data?.first_name && response.data?.data?.last_name;
          shouldRedirectToUserInfo = !hasProfileInDB;
        }
      } else {
        shouldRedirectToUserInfo =
          !userResult.data?.user?.fullName && !userInfoUpdatedFlag;
      }

      if (!isRedirectToUserInfo && shouldRedirectToUserInfo) {
        isRedirectToUserInfo = true;
        dispatch(setRedirectToUserInfo(true));
      }

      if (selectedClinic) {
        timeProvider.setTimezone(selectedClinic.timezone || "");
        dispatch(setUserClinic({ clinic: selectedClinic }));

        // ── Phase 3: Parallel calls that all need selectedClinic ──
        const homeHealthPromise = (async () => {
          let patientId: number | null = null;
          if (
            selectedClinic?.is_home_health_enabled &&
            !userResult.data?.user?.homeHealthId
          ) {
            const res =
              await homeHealthController.getSelectedFacilityPatientList({
                clinic_id: selectedClinic.id,
              });
            const { success: homeHealthSuccess, data: homeHealthData } = res;
            if (homeHealthSuccess && homeHealthData) {
              const patientList = homeHealthData as unknown as IHHPatient[];
              const c100DeviceIdDictionary = patientList.reduce(
                (acc: Dictionary<IHHPatient>, item: IHHPatient) => {
                  const c100DeviceId = item.devices?.find(
                    (device: any) => device.device_type === "c100"
                  );
                  if (c100DeviceId) {
                    acc[c100DeviceId.device_id] = item;
                  }
                  return acc;
                },
                {}
              );
              dispatch(
                setHomehealthState({
                  patients: patientList,
                  c100DeviceIdDictionary,
                })
              );
            }
          } else if (
            selectedClinic?.is_home_health_enabled &&
            userResult.data?.user?.homeHealthId
          ) {
            const res = await homeHealthController.getSinglePatientInfo({
              patient_id: userResult.data?.user?.homeHealthId,
            });
            if (res.success && res.data) {
              const c100DeviceIdDictionary: Dictionary<IHHPatient> = {};
              res.data.devices.forEach((item) => {
                if (item.device_type === "c100") {
                  item._id = res.data?._id;
                  c100DeviceIdDictionary[item.device_id] = item;
                  patientId = item.device_id;
                }
              });
              dispatch(
                setHomehealthState({
                  patients: [
                    {
                      ...res.data,
                      patient_id: patientId,
                    } as unknown as IHHPatient,
                  ],
                  c100DeviceIdDictionary,
                })
              );
              window.location.replace(`/#/patient-profile/${patientId}`);
            }
          }
        })();

        const notificationSettingsPromise = (async () => {
          if (!featureFlag.notificationSettings) return null;
          const isNewUser = localStorageProvider.get("isNewUser");
          if (!response.data) return null;

          let notificationRes = null;
          if (isNewUser) {
            const defaultConfig = {
              email: false,
              pushNotification: true,
              kioskMode: false,
              escalatedAlertPreference: initialEscalatedAlertLevels.map(
                (item) => item.value
              ),
              facilitiesToSendNotificationForOldId: [selectedClinic.id],
              psnAlertPreference: ["PsnLevel3Acute", "PsnLevel3"],
              bedExitPreference: [],
              sms: false,
              smsPreferences: {
                morning_report: false,
                afternoon_report: false,
                evening_report: false,
                just_in_time: false,
              },
            };
            notificationRes =
              await notificationController.setNotificationSetting(
                defaultConfig
              );
            dispatch(setNotificationSettings(defaultConfig));
            localStorageProvider.remove("isNewUser");
          } else {
            notificationRes =
              await notificationController.setNotificationSetting({
                email: response.data.data?.notification_preference?.email,
                pushNotification:
                  response.data.data?.notification_preference
                    ?.push_notification !== false,
                kioskMode:
                  response.data.data?.notification_preference?.kiosk_mode,
                escalatedAlertPreference:
                  response.data.data?.notification_preference
                    ?.escalated_alert_preference,
                facilitiesToSendNotificationForOldId: [selectedClinic.id],
                psnAlertPreference:
                  response.data.data?.notification_preference
                    ?.psn_alert_preference,
                bedExitPreference:
                  response.data.data?.notification_preference
                    ?.bed_exit_preference,
                sms: response.data.data?.notification_preference?.sms,
                smsPreferences:
                  response.data.data?.notification_preference?.sms_preferences,
              });

            const autoBedExitPrefs = (
              response.data.data?.notification_preference as any
            )?.auto_bed_exit_preference as
              | Array<{
                  clinic_id: number;
                  is_enabled: boolean;
                }>
              | undefined;
            const autoBedExit72h =
              autoBedExitPrefs?.find(
                (pref) => pref.clinic_id === selectedClinic?.id
              )?.is_enabled || false;

            dispatch(
              setNotificationSettings({
                email: response.data.data?.notification_preference?.email,
                pushNotification:
                  response.data.data?.notification_preference
                    ?.push_notification !== false,
                kioskMode:
                  response.data.data?.notification_preference?.kiosk_mode,
                escalatedAlertPreference:
                  response.data.data?.notification_preference
                    ?.escalated_alert_preference,
                facilitiesToSendNotificationForOldId: [selectedClinic.id],
                psnAlertPreference:
                  response.data.data?.notification_preference
                    ?.psn_alert_preference,
                bedExitPreference:
                  response.data.data?.notification_preference
                    ?.bed_exit_preference,
                sms: response.data.data?.notification_preference?.sms,
                smsPreferences:
                  response.data.data?.notification_preference?.sms_preferences,
                autoBedExit72h,
              })
            );
          }
          return notificationRes;
        })();

        const floorPromise = skipClinicFloorPref
          ? Promise.resolve(null)
          : clinicController.getClinicFloor({
              clinicId: selectedClinic.id,
            });

        const [, notificationSaveResult, floorResult] = await Promise.all([
          homeHealthPromise,
          notificationSettingsPromise,
          floorPromise,
        ]);

        // Preserve early-return on notification save error
        if (notificationSaveResult?.error) {
          appNotification({
            nTitle: notificationSaveResult.error.message,
            nType: AppNotificationType.ERROR,
          });
          return;
        }
        if (notificationSaveResult?.data) {
          dispatch(
            setAuthState({
              userPreference: notificationSaveResult.data
                .data as unknown as UserPreference,
            })
          );
        }

        // ── Phase 4: Process floors, then parallel watchlist + summary ──
        if (skipClinicFloorPref) {
          dispatch(setClinicFloorList({ clinicFloorList: [] }));
          dispatch(setSelectedFloors([]));
          dispatch(setDashboardFloor([]));
          dispatch(setClinicalAlertFilter({ floors: [] }));
        } else {
          const { success: floorSuccess, data: floorData } = floorResult ?? {};

          if (floorSuccess && floorData) {
            dispatch(setClinicFloorList({ clinicFloorList: floorData }));
            const selectedFloors: number[] = [];
            floorData.forEach((item) => {
              if (item.enabled) selectedFloors.push(item.floor_id);
            });
            const allClinicFloors = floorData.map((item) => item.floor_id);
            let selectFloors = selectedFloors;
            if (floorData.length > 0 && selectedFloors.length === 0) {
              const Allfloors = await clinicController.setClinicFloor({
                clinicId: selectedClinic.id,
                floor_ids: floorData.map((item) => item.floor_id),
              });
              selectFloors = Allfloors.data?.floors || [];
              dispatch(
                setClinicFloorList({
                  clinicFloorList: floorData.map((item) => ({
                    ...item,
                    enabled: true,
                  })),
                })
              );
            }
            dispatch(setSelectedFloors(selectFloors));
            if (
              changeFacility ||
              selectFloors.length >= dashboardFloor.length
            ) {
              dispatch(setDashboardFloor(selectFloors));
              dispatch(
                setClinicalAlertFilter({
                  floors: selectFloors.map((item) => Number(item)),
                })
              );
            }

            // Watchlist + Summary in parallel
            const [watchlistResult, summaryResult] = await Promise.all([
              watchlistController.getWatchlist({
                clinicId: selectedClinic.id,
                floorIds: allClinicFloors,
              }),
              selectFloors && selectFloors.length > 0
                ? summaryController.getSummary({
                    clinicId: selectedClinic.id,
                    floorIds: selectFloors,
                    timezone: selectedClinic.timezone || "",
                  })
                : Promise.resolve(null),
            ]);

            if (watchlistResult.success && watchlistResult.data) {
              dispatch(
                updateWatchlist({
                  watchlist: watchlistResult.data.data || [],
                })
              );
            }
            if (summaryResult?.success && summaryResult?.data) {
              dispatch(setSummaryState({ summary: summaryResult.data }));
            }
          }
        }
      }
      dispatch(setClinicState({ clinicState: "pending" }));
    }

    // Intercom login (reuses Phase 1 getCurrentUser + getPatientHmac results)
    // Skip for home health external users — they don't use Intercom
    if (
      user &&
      hmac &&
      clinicData?.length &&
      !isHomeHealthExternalUser(user?.groups)
    ) {
      const selectedClinicForIntercom = clinicController.getSelectedClinic({
        clinicList: clinicData,
      });
      if (
        selectedClinicForIntercom &&
        selectedClinicForIntercom?.name &&
        user.fullName
      ) {
        const token = await authController.getAuthenticationToken();
        circadiaEngineProvider.loginIntercomUser(token, hmac, {
          name: user.fullName,
          user_id: user.userID,
          email: user.email,
          facility: selectedClinicForIntercom?.name,
        });
      } else if (user.userID) {
        const token = await authController.getAuthenticationToken();
        circadiaEngineProvider.loginIntercomUser(token, hmac, {
          name: user.fullName || "",
          user_id: user.userID,
          email: user.email || "",
          facility: selectedClinicForIntercom?.name || "",
        });
      }
    }

    dispatch(
      setLayoutState({
        layoutState: "pending",
      })
    );
  };

  const getAuthState = async () => {
    dispatch(
      setAuthState({
        authState: "loading",
      })
    );

    // Z-Pax Auth Integration will be handled by useZpaxAuth hook in components
    // The hook handles detection, callback parsing, and token exchange
    // Normal auth flow continues here

    const { data, error } = await authController.getCurrentUser();
    if (error && error === "pinRequired") {
      dispatch(
        setAuthState({
          isAuthorized: true,
          authState: "pending",
        })
      );
      return;
    }

    if (!data || !data.user) {
      dispatch(
        setAuthState({
          authState: "pending",
          isAuthorized: false,
          isPinAuthorized: false,
        })
      );
      return "notAuthorized";
    }
    const isCredentials = localStorageProvider.get("internalUse");
    const isBiometric = localStorageProvider.get("isBiometric");
    if (data && data.user && isPIN() ? isCredentials : true) {
      localStorageProvider.set(
        "username",
        `${data.user?.firstName} ${data.user?.lastName || ""}`
      );

      const hasNoAccess = (data.user.groups || []).length < 1;
      if (isBiometric && isBiometric === "true") {
        dispatch(
          setAuthState({
            user: data.user,
            isAuthorized: true,
            isPinAuthorized: true,
            hasNoAccess,
            authState: "pending",
          })
        );
        return "biometric";
      } else {
        dispatch(
          setAuthState({
            user: data.user,
            isAuthorized: true,
            hasNoAccess,
            authState: "pending",
          })
        );
      }
      return;
    }

    dispatch(
      setAuthState({
        authState: "pending",
      })
    );
  };

  return {
    populateAppData,
    getAuthState,
    storedBiometricValue,
  };
};
